import rp from "@reporting/rp-app-host";
import { ReportResult } from "../../model/reportResult";
import { VisualizationConfig } from "../../types";
import { BaseVisualization } from "./baseVisualization";
import { GaugeVisualization } from "./gauge";
import { TileVisualization } from "./tile";
import { dom } from '@omegagrid/core';
import { TableAdapter } from "./tableAdapter";
import { AnalyticsApplication, BaseVisualizationEditor, BlankVisualization, ChartVisualizationEditor, DashboardVisualizationEditor, GaugeVisualizationEditor, TableVisualizationEditor } from "../..";
import { TableVisualization } from "./table";
import { cloneContext, isMultiContext, isTransactionContext, isExtensionContext, findItemIndexByUid } from "../../common";
import { MultiContextDashboardAdapter, SingleContextDashboardAdapter } from "./dashboardAdapter";
import { DashboardVisualization } from "./dashboard";
import { MultiContextSpreadsheetAdapter } from "./spreadsheetAdapter";
import { SpreadsheetVisualization } from "./spreadsheet";
import { ExtensionVisualization } from "./extension";
import { ContextData, MultiContextData, ReportContext } from "@reporting/rp-types/analytics";
import { ChartFactory } from "./chartFactory";
import { TransactionVisualization } from "./transactions";
import { TransactionAdapter } from "./transactionAdapter";
import { TextVisualization } from "./text";
import { TextVisualizationEditor } from "./editors/textEditor";
import { LinksVisualization } from "./links";
import { LinksVisualizationEditor } from "./editors/linksEditor";

const app = () => rp.applications.get('analytics') as AnalyticsApplication;

const createTransactionVisualization = (reportContext: ReportContext, result: ReportResult) => [
	dom.createElement<TransactionVisualization>('rp-visual-transactions', {
		adapter: new TransactionAdapter(reportContext, app().reportBroker),
	})
];

const createTableVisualization = (reportContext: ReportContext, result: ReportResult) => [
	dom.createElement<TableVisualization>('rp-visual-table', {
		adapter: new TableAdapter(result, reportContext),
	})
];

const createTableVisualizationEditor = (reportContext: ReportContext) =>
	dom.createElement<TableVisualizationEditor>('rp-visual-table-editor', { reportContext });

const createChartVisualization = (reportContext: ReportContext, result: ReportResult) =>
	new ChartFactory(result, reportContext).createCharts();

const createChartVisualizationEditor = (reportContext: ReportContext, result: ReportResult) =>
	dom.createElement<ChartVisualizationEditor>('rp-visual-chart-editor', { reportContext, result });

const createGaugeVisualization = (reportContext: ReportContext, result: ReportResult) => [
	dom.createElement<GaugeVisualization>('rp-visual-gauge', { reportContext, result })
];

const createGaugeVisualizationEditor = (reportContext: ReportContext, result: ReportResult) =>
	dom.createElement<GaugeVisualizationEditor>('rp-visual-gauge-editor', { reportContext, result });

const createTileVisualization = (reportContext: ReportContext, result: ReportResult) => [
	dom.createElement<TileVisualization>('rp-visual-tile', { result })
];

const createBlankVisualization = (reportContext: ReportContext, result: ReportResult) => [
	dom.createElement<BlankVisualization>('rp-visual-blank', { result })
];

const createTextVisualization = (reportContext: ReportContext) => [
	dom.createElement<TextVisualization>('rp-visual-text')
];

const createTextVisualizationEditor = (reportContext: ReportContext) =>
	dom.createElement<TextVisualizationEditor>('rp-visual-text-editor', { reportContext });

const createLinksVisualization = (reportContext: ReportContext) => [
	dom.createElement<LinksVisualization>('rp-visual-links')
];

const createLinksVisualizationEditor = (reportContext: ReportContext) =>
	dom.createElement<LinksVisualizationEditor>('rp-visual-links-editor', { reportContext });

const createDashboardVisualization = (reportContext: ReportContext) => [
	dom.createElement<DashboardVisualization>('rp-visual-dashboard', {
		adapter: new MultiContextDashboardAdapter(app().getOrCreateReportStore(reportContext)),
		createComponent: (id) => rp.host.createComponent(id)
	})
];

const createDashboardVisualizationEditor = (reportContext: ReportContext, result: ReportResult) =>
	dom.createElement<DashboardVisualizationEditor>('rp-visual-dashboard-editor', { reportContext, result });

const createSingleContextDashboardVisualization = (reportContext: ReportContext, components: BaseVisualization[]) =>
	dom.createElement<DashboardVisualization>('rp-visual-dashboard', {
		contextData: reportContext.context as ContextData,
		// the items render the same report, the editor grid works with its data
		reportData: components[0]?.reportData,
		createComponent: (id) => components[id as number],
		adapter: new SingleContextDashboardAdapter(
			reportContext,
			components.map(c => ({title: c.caption}))
		)
	});

const createSpreadsheetVisualization = (reportContext: ReportContext) => [
	dom.createElement<SpreadsheetVisualization>('rp-visual-spreadsheet', {
		adapter: new MultiContextSpreadsheetAdapter(app().getOrCreateReportStore(reportContext)),
	})
];

export const defaultVisualizations: VisualizationConfig[] = [{
	id: 'table',
	aliases: ['table1', 'table2', 'table3'],
	type: 'table',
	name: 'visual.table',
	icon: 'table',
	editorWidth: 300,
	factory: (options, result) => createTableVisualization(options, result),
	editorFactory: (options) => createTableVisualizationEditor(options)
}, {
	id: 'transactions',
	type: 'table',
	name: 'visual.transactions',
	icon: 'list',
	factory: (options, result) => createTransactionVisualization(options, result)
}, {
	id: 'column',
	aliases: ['bar_g'],
	type: 'chart',
	name: 'visual.column',
	icon: 'chart-column',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'column-stacked',
	aliases: ['bar_s'],
	type: 'chart',
	name: 'visual.column-stacked',
	icon: 'chart-column',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'bar',
	aliases: ['bar_h_g'],
	type: 'chart',
	name: 'visual.bar',
	icon: 'chart-bar',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'bar-stacked',
	aliases: ['bar_h_s'],
	type: 'chart',
	name: 'visual.bar-stacked',
	icon: 'chart-bar',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'line',
	aliases: ['line2'],
	type: 'chart',
	name: 'visual.line',
	icon: 'chart-line',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'spline',
	type: 'chart',
	name: 'visual.spline',
	icon: 'chart-sine',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'area',
	type: 'chart',
	name: 'visual.area',
	icon: 'chart-area',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'pie',
	aliases: ['donut'],
	type: 'chart',
	name: 'visual.pie',
	icon: 'chart-pie',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'scatter',
	type: 'chart',
	name: 'visual.scatter',
	icon: 'chart-scatter',
	section: 'Charts',
	factory: (options, result) => createChartVisualization(options, result),
	editorFactory: (options, result) => createChartVisualizationEditor(options, result)
}, {
	id: 'gauge',
	type: 'gauge',
	name: 'visual.gauge',
	icon: 'gauge',
	section: 'Charts',
	factory: (options, result) => createGaugeVisualization(options, result),
	editorFactory: (options, result) => createGaugeVisualizationEditor(options, result),
}/*, {
	id: 'tile',
	type: 'tile',
	name: 'visual.tile',
	icon: 'rectangle-list',
	section: 'Charts',
	factory: (options, result) => createTileVisualization(options, result)
}*/, {
	id: 'blank',
	type: 'blank',
	name: 'visual.blank',
	icon: 'file',
	section: 'Charts',
	factory: (options, result) => createBlankVisualization(options, result)
}];

/**
 * Text item without any connection to the data model. Not listed in
 * defaultVisualizations so it cannot be selected as a standalone view,
 * it is only added to dashboards via the "+" toolbar menu.
 */
export const textVisualization: VisualizationConfig = {
	id: 'text',
	type: 'text',
	name: 'visual.text',
	icon: 'text',
	noData: true,
	factory: (reportContext) => createTextVisualization(reportContext),
	editorFactory: (reportContext) => createTextVisualizationEditor(reportContext),
};

/**
 * List of links to other saved views, only available as a dashboard item
 * (see textVisualization above for the concept).
 */
export const linksVisualization: VisualizationConfig = {
	id: 'links',
	type: 'links',
	name: 'visual.links',
	icon: 'link',
	noData: true,
	factory: (reportContext) => createLinksVisualization(reportContext),
	editorFactory: (reportContext) => createLinksVisualizationEditor(reportContext),
};

export const defaultMultiVisualizations: VisualizationConfig[] = [{
	id: 'D',
	type: 'dashboard',
	name: 'visual.dashboard',
	icon: 'cubes',
	factory: (reportContext) => createDashboardVisualization(reportContext),
	editorFactory: (options, result) => createDashboardVisualizationEditor(options, result),
	editorWidth: 300,
}, {
	id: 'S',
	type: 'spreadsheet',
	name: 'visual.spreadsheet',
	icon: 'table',
	factory: (reportContext) => createSpreadsheetVisualization(reportContext),
}];

let visualizationMap: Map<string, VisualizationConfig>;
export const getVisualizationMap = () => {
	if (visualizationMap) return visualizationMap;
	visualizationMap = new Map();
	[...defaultVisualizations, ...defaultMultiVisualizations, textVisualization, linksVisualization].forEach(v => {
		visualizationMap.set(v.id, v);
		if (v.aliases) v.aliases.forEach(alias => visualizationMap.set(alias, v));
	});
	return visualizationMap;
}

export const getVisualizationConfig = (ctx: string|ContextData|MultiContextData) => {
	let view = typeof ctx === 'string' ? ctx : (ctx as ContextData)?.view;
	view = /^table/i.test(view) ? 'table' : view;
	let config = getVisualizationMap().get(view) ?? null;
	if (!config && (ctx as ContextData)?.module) {
		config = getVisualizationMap().get((ctx as ContextData).module) ?? null;
	}
	return config;
}

type CreateVisualizationOptions = {
	context: ReportContext;
	itemIndex?: number;
	/** stable runtime id of a dashboard item, alternative to itemIndex */
	itemUid?: string;
	parentIdContext?: number;
	reloadContext?: boolean;
}

export async function createExtensionVisualization(reportContext: ReportContext): Promise<BaseVisualization> {
	return dom.createElement<ExtensionVisualization>('rp-visual-extension', { reportContext });
}

export async function createVisualization(options: CreateVisualizationOptions) : Promise<BaseVisualization> {
	let reportData: ReportResult;
	let config: VisualizationConfig;
	let reportContext = options.context;

	if (!reportContext) return null;

	if (options.reloadContext) {
		// force contextBroker to reload context
		app().contextBroker.invalidateContext(reportContext);
		if (reportContext.id_context) {
			reportContext = { id_context: reportContext.id_context };
		}
	}

	let itemIndex = options.itemIndex;
	const isItem = itemIndex != null || options.itemUid != null;
	const parentContext = isItem ? reportContext : null;

	if (reportContext.context && !options.reloadContext) {
		// full context passed by the caller (store draft or container working
		// copy): use it directly - re-resolving it through the broker would
		// hand out the cached instance and the visualization would work on
		// a different object than its container (the dual instance problem)
	} else {
		reportContext = await app().contextBroker.getContext(reportContext);
		const store = app().getReportStore(reportContext?.id_context);
		if (store && reportContext) {
			// bare id resolved while a store exists (e.g. reload): sync the
			// store and render from it - dashboards use the draft directly,
			// single reports get a mutable working copy
			if (options.reloadContext) store.resetTo(reportContext);
			reportContext = isMultiContext(store.context)
				? store.context
				: cloneContext(store.context);
		}
	}

	if (reportContext && isMultiContext(reportContext) && isItem) {
		// resolve the item uid carried in the component id to its current index
		if (itemIndex == null) {
			itemIndex = findItemIndexByUid(reportContext.context as MultiContextData, options.itemUid);
		}
		reportContext = await app().contextBroker.getSubcontext(reportContext, itemIndex);
	}

	// subcontext may be missing (e.g. item removed or index out of range), render nothing
	if (!reportContext) return null;

 	if (isExtensionContext(reportContext)) {
		return createExtensionVisualization(reportContext);
	} else if (isMultiContext(reportContext)) {
		config = getVisualizationMap().get(reportContext.context?.module);
	} else {
		config = getVisualizationConfig((reportContext.context as ContextData)?.view ?? 'table');
		if (!isTransactionContext(reportContext) && !config?.noData) {
			reportData = await app().reportBroker.getReportData(reportContext, {
				parentContext: parentContext,
				history: itemIndex == null || itemIndex === 0
			});
		}
	}

	if (!config?.factory) return null;

	const components = config.factory(reportContext, reportData).map(component => Object.assign(component, {
		app: app(),
		contextData: reportContext.context,
		reportData: reportData,
	}));

	return components.length > 1 ? createSingleContextDashboardVisualization(reportContext, components) : components[0];
}

export async function createVisualizationEditor(options: CreateVisualizationOptions) : Promise<BaseVisualizationEditor> {
	let reportContext = options.context;
	let config = getVisualizationConfig((reportContext.context as ContextData)?.view)
		?? getVisualizationConfig(reportContext.module as string);

	if (!config?.editorFactory) return null;
	return config.editorFactory(reportContext);
}
